import { useState, useEffect } from "react";
import axios from "axios";

axios.defaults.withCredentials = true;

export default function CommentSection({ company }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [userSpecificComments, setUserSpecificComments] = useState([]);
  const [isPressed, setIsPressed] = useState(false);

  // ✅ 세션 유저 정보
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      axios
        .get(`${process.env.REACT_APP_SPRING_API_URL}/api/session-user`)
        .then((res) => setUserInfo(res.data))
        .catch(() => setUserInfo(null));
    } else {
      setUserInfo(null);
    }
  }, []);

  // ✅ 댓글 목록 조회 (likes/dislikes/userReaction 포함)
  const fetchComments = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SPRING_API_URL}/api/${company}/comments`,
        { params: { page: 0, size: 50 } },
      );

      const list = res.data?.content ?? [];
      setComments(list);
    } catch (err) {
      console.error("댓글 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [company]);

  // ✅ 프로필 팝업
  const toggleProfileBox = async (commentId, username) => {
    if (activeProfile === commentId) {
      setActiveProfile(null);
      setUserSpecificComments([]);
    } else {
      setActiveProfile(commentId);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_SPRING_API_URL}/api/user/${username}/comments`,
        );
        const list = res.data?.content ?? res.data ?? [];
        setUserSpecificComments(list);
      } catch (err) {
        console.error("유저 댓글 로드 실패:", err);
      }
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return alert("댓글 내용을 입력해주세요.");
    try {
      await axios.post(
        `${process.env.REACT_APP_SPRING_API_URL}/api/${company}/comments`,
        { content: newComment },
      );
      setNewComment("");
      fetchComments();
    } catch (err) {
      console.error("댓글 추가 실패:", err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_SPRING_API_URL}/api/${company}/comments/${commentId}`,
      );
      fetchComments();
      if (activeProfile) setActiveProfile(null);
    } catch (err) {
      alert("삭제 권한이 없거나 오류가 발생했습니다.");
      console.error("댓글 삭제 실패:", err);
    }
  };

  const handleReaction = async (commentId, type) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_SPRING_API_URL}/api/${commentId}/reactions/${type.toLowerCase()}`,
      );
      fetchComments(); // 반응 후 다시 갱신
    } catch (err) {
      console.error(`${type} 실패:`, err);
    }
  };

  const getButtonStyle = (comment, type) => {
    if (comment.userReaction === type)
      return { color: type === "LIKE" ? "blue" : "red", fontWeight: "bold" };
    return {};
  };

  return (
    <div
      style={{
        border: "2px solid black",
        padding: 10,
        width: 1000,
        background: "white",
        marginBottom: "50px",
      }}
    >
      <h3 style={{ textShadow: "none", color: "black" }}>{company} 댓글</h3>

      {userInfo ? (
        <div
          style={{
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <input
            type="text"
            placeholder="댓글 입력"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{
              flex: 1, // ✅ 남는 공간 전부 사용
              border: "2px solid black",
              backgroundColor: "white",
              color: "black",
              padding: "8px 10px",
              outline: "none",
              borderRadius: "6px",
              minWidth: 0, // ✅ flex에서 글자 넘침 방지
            }}
            onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
          />
          <button
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onMouseLeave={() => setIsPressed(false)}
            onClick={handleAddComment}
            style={{
              flexShrink: 0,
              padding: "8px 14px",
              border: "2px solid black",
              backgroundColor: isPressed ? "#333" : "black", // 👈 눌리면 어두워짐
              color: "white",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              transform: isPressed ? "scale(0.96)" : "scale(1)", // 👈 눌림 애니메이션
              boxShadow: isPressed
                ? "inset 2px 2px 4px rgba(0,0,0,0.5)"
                : "2px 2px 4px rgba(0,0,0,0.3)",
              transition: "all 0.08s ease",
            }}
          >
            작성
          </button>
        </div>
      ) : (
        <p>로그인 후 댓글 작성이 가능합니다.</p>
      )}

      {comments.map((c) => (
        <div
          key={c.id}
          style={{
            borderBottom: "1px solid #070606",
            padding: "10px 0",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <strong
              onClick={() => toggleProfileBox(c.id, c.username)}
              style={{ cursor: "pointer" }}
            >
              {c.username}
            </strong>

            {activeProfile === c.id && userInfo?.isAdmin && (
              <div
                style={{
                  position: "absolute",
                  top: "30px",
                  left: "0px",
                  width: "350px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  background: "white",
                  border: "2px solid black",
                  zIndex: 1000,
                }}
              >
                {userSpecificComments.map((uc, idx) => (
                  <div key={idx}>
                    <div>{uc.content}</div>
                  </div>
                ))}
              </div>
            )}

            <span style={{ marginLeft: "5px" }}>: {c.content}</span>

            {userInfo &&
              (c.username === userInfo.username || userInfo.isAdmin) && (
                <button
                  onClick={() => handleDeleteComment(c.id)}
                  style={{ marginLeft: 10, color: "red" }}
                >
                  삭제
                </button>
              )}
          </div>

          <div style={{ fontSize: 12 }}>
            <button
              style={{
                ...getButtonStyle(c, "LIKE"),
                background: "white",
                border: "1px solid #ccc",
                padding: "4px 8px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
              onClick={() => handleReaction(c.id, "LIKE")}
            >
              👍 <span style={{ color: "black" }}>{c.likes}</span>
            </button>
            <button
              style={{
                ...getButtonStyle(c, "DISLIKE"),
                background: "white",
                border: "1px solid #ccc",
                padding: "4px 8px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
              onClick={() => handleReaction(c.id, "DISLIKE")}
            >
              👎 <span style={{ color: "black" }}>{c.dislikes}</span>
            </button>
          </div>

          <div style={{ fontSize: 15, color: "#e0d7d7" }}>
            {new Date(c.createdAt).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}
